/**
 * Smart toast — theme-aware, deduplicates, max 2 visible at once.
 * ALL files must import from here — never from 'react-hot-toast' directly.
 */
import { toast as _toast } from 'react-hot-toast';

// ── Track active toasts ──────────────────────────────────────────────────────
const active     = new Map();
const keyToId    = new Map();
const recentKeys = new Map();
const DEBOUNCE_MS = 600;

function getKey(msg) {
  if (typeof msg === 'string') return msg.trim().slice(0, 80);
  return 'toast_' + Math.random();
}

function isDuplicate(key) {
  const last = recentKeys.get(key);
  if (last && Date.now() - last < DEBOUNCE_MS) return true;
  recentKeys.set(key, Date.now());
  setTimeout(() => recentKeys.delete(key), DEBOUNCE_MS + 100);
  return false;
}

// Priority tiers: 'critical' never dismissed, 'transaction' kept over 'passive'
const PRIORITY = { critical: 3, transaction: 2, passive: 1 };

function enforceLimit(incomingPriority = 'passive') {
  if (active.size < 3) return; // Allow up to 3 toasts
  // Find lowest priority active toast to dismiss
  let lowestId = null, lowestPri = 99;
  active.forEach((val, id) => {
    const pri = PRIORITY[val.priority] || 1;
    if (pri < lowestPri) { lowestPri = pri; lowestId = id; }
  });
  // Only dismiss if incoming is higher or equal priority
  const inPri = PRIORITY[incomingPriority] || 1;
  if (lowestId && inPri >= lowestPri) {
    const oldKey = active.get(lowestId).key;
    _toast.dismiss(lowestId);
    active.delete(lowestId);
    keyToId.delete(oldKey);
  }
}

function register(id, key, duration, priority = 'passive') {
  active.set(id, { key, priority });
  keyToId.set(key, id);
  setTimeout(() => { active.delete(id); keyToId.delete(key); }, (duration || 2800) + 400);
}

// ── Theme detection ──────────────────────────────────────────────────────────
// Reads --text-primary directly from computed CSS — works for ALL themes
// (data-theme="light", data-user-theme="royal", data-user-theme="emerald", etc.)
function isLightTheme() {
  const html = document.documentElement;
  // data-theme="light" = light theme
  if (html.getAttribute('data-theme') === 'light') return true;
  // No data-user-theme and no data-theme = default dark
  const userTheme = html.getAttribute('data-user-theme');
  if (!userTheme) return false;
  // These user themes are light backgrounds
  return userTheme === 'light';
}

function getColors() {
  const light = isLightTheme();
  return light ? {
    text:       '#111827',    // near-black for light theme — readable on all light backgrounds
    successBg:  '#065f46',
    errorBg:    '#fff1f2',
    loadingBg:  '#f5f3ff',
    defaultBg:  '#ffffff',
    borderSucc: 'rgba(5,150,105,0.30)',
    borderDang: 'rgba(225,29,72,0.30)',
    border:     'rgba(20,184,166,0.20)',
    shadow:     '0 8px 32px rgba(99,102,241,0.15)',
  } : {
    text:       '#f1f5f9',    // hardcoded light — never from CSS var (could be white in dark-card)
    successBg:  '#065f46',
    errorBg:    '#1f0a0a',
    loadingBg:  '#0f1623',
    defaultBg:  '#1a1d2e',
    borderSucc: 'rgba(16,185,129,0.35)',
    borderDang: 'rgba(239,68,68,0.35)',
    border:     'rgba(20,184,166,0.25)',
    shadow:     '0 12px 40px rgba(0,0,0,0.5)',
  };
}

function makeStyle(bg, border, c) {
  return {
    background:          bg,
    color:               c.text,
    WebkitTextFillColor: c.text,   // override any CSS that sets text fill to white
    border:              `1px solid ${border}`,
    borderRadius:        '16px',
    fontSize:            '13px',
    fontWeight:          '600',
    maxWidth:            '360px',
    fontFamily:          'Outfit, sans-serif',
    padding:             '12px 16px',
    boxShadow:           c.shadow,
  };
}

function makeSuccessStyle(c) {
  return {
    ...makeStyle(c.successBg, c.borderSucc, c),
    color: '#ffffff',
    WebkitTextFillColor: '#ffffff',
    fontWeight: '700',
  };
}

// ── Public API ───────────────────────────────────────────────────────────────
const toast = Object.assign(
  function(msg, opts) {
    const key = opts?.id || getKey(msg);
    if (isDuplicate(key)) return key;
    if (keyToId.has(key)) { _toast.dismiss(keyToId.get(key)); active.delete(keyToId.get(key)); keyToId.delete(key); }
    const priority = opts?.priority || 'passive';
    enforceLimit(priority);
    const c  = getColors();
    const id = _toast(msg, { id: key, style: makeStyle(c.defaultBg, c.border, c), ...opts });
    register(id, key, opts?.duration, priority);
    return id;
  },
  {
    success(msg, opts) {
      const key = opts?.id || ('s_' + getKey(msg));
      if (isDuplicate(key)) return key;
      if (keyToId.has(key)) { _toast.dismiss(keyToId.get(key)); active.delete(keyToId.get(key)); keyToId.delete(key); }
      enforceLimit();
      const c  = getColors();
      const id = _toast.success(msg, {
        id: key,
        duration: 2500,
        style: makeSuccessStyle(c),
        iconTheme: { primary: '#ffffff', secondary: c.successBg },
        ...opts,
      });
      register(id, key, opts?.duration ?? 2500);
      return id;
    },

    error(msg, opts) {
      const key = opts?.id || ('e_' + getKey(msg));
      if (isDuplicate(key)) return key;
      if (keyToId.has(key)) { _toast.dismiss(keyToId.get(key)); active.delete(keyToId.get(key)); keyToId.delete(key); }
      enforceLimit();
      const c  = getColors();
      const id = _toast.error(msg, {
        id: key,
        duration: 3500,
        style: makeStyle(c.errorBg, c.borderDang, c),
        iconTheme: { primary: '#ef4444', secondary: c.errorBg },
        ...opts,
      });
      register(id, key, opts?.duration ?? 3500);
      return id;
    },

    loading(msg, opts) {
      const key = opts?.id || ('l_' + getKey(msg));
      if (keyToId.has(key)) { _toast.dismiss(keyToId.get(key)); active.delete(keyToId.get(key)); keyToId.delete(key); }
      enforceLimit();
      const c  = getColors();
      const id = _toast.loading(msg, {
        id: key,
        style: makeStyle(c.loadingBg, c.border, c),
        iconTheme: { primary: '#6366f1', secondary: c.loadingBg },
        ...opts,
      });
      register(id, key, opts?.duration ?? 30000);
      return id;
    },

    dismiss(id) {
      if (id) {
        const key = active.get(id);
        if (key) { active.delete(id); keyToId.delete(key); }
      } else {
        active.clear(); keyToId.clear();
      }
      _toast.dismiss(id);
    },

    promise: _toast.promise,
    custom:  _toast.custom,
    remove:  _toast.remove,
  }
);

export default toast;
